import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Grid,
  Paper,
  Alert,
  CircularProgress
} from '@mui/material';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../firebase-config';
import { getAuth } from 'firebase/auth';

const IntroMessageStep = ({ data, onUpdate, onNext, onBack }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  // Get business name from data if available
  const businessName = data.businessInfo?.name || '';

  // Create default message with business name if available
  const createDefaultMessage = () => {
    if (businessName) {
      return `${businessName}. Sorry I missed your call! I am currently with a customer. How can I help you today?`;
    }
    return "Sorry I missed your call! I am currently with a customer. How can I help you today?";
  };

  const [introMessage, setIntroMessage] = useState(
    data.introMessage || createDefaultMessage()
  );

  // Get the current user ID
  const auth = getAuth();
  const userId = auth.currentUser?.uid;

  const handleChange = (e) => {
    setIntroMessage(e.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (!userId) {
        throw new Error("User ID is missing. Please try logging in again.");
      }

      // Validate intro message (required field)
      if (!introMessage.trim()) {
        throw new Error("Please enter an intro message.");
      }

      // Create the document reference and data
      const businessRef = doc(db, 'businesses', userId);

      // Save to Firestore
      await setDoc(businessRef, {
        introMessage,
        updatedAt: new Date().toISOString()
      }, { merge: true });

      setSuccess(true);

      // Update parent component
      onUpdate({
        ...data,
        introMessage
      });

      // Wait a moment to show success message
      setTimeout(() => {
        onNext();
      }, 1000);
    } catch (err) {
      console.error('Error saving intro message:', err);
      setError(err.message || 'Failed to save intro message');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ maxWidth: 800, mx: 'auto' }}>
      <Typography variant="h6" gutterBottom>
        Introduction Message
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>Introduction message saved successfully!</Alert>}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="body2" sx={{ mb: 2 }}>
              This is the first message that will be sent to customers when they call and no one answers.
              We've included your business name in the default message, but you can customize it to fit your business style.
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <TextField
              required
              fullWidth
              label="Introduction Message"
              value={introMessage}
              onChange={handleChange}
              multiline
              rows={4}
              helperText="Your business name is automatically included in the default message"
            />
          </Grid>
        </Grid>
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
        <Button
          variant="outlined"
          onClick={onBack}
          disabled={loading}
        >
          Back
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={loading || !introMessage.trim()}
        >
          {loading ? <CircularProgress size={24} /> : 'Next'}
        </Button>
      </Box>
    </Box>
  );
};

export default IntroMessageStep;